import { Platform } from 'react-native';

// Platform-specific Stripe hook
export function useStripe() {
  if (Platform.OS === 'web') {
    // Use web Stripe implementation
    try {
      const { useStripe: useStripeWeb } = require('../components/StripeProviderWeb');
      return useStripeWeb();
    } catch (e) {
      console.warn('[useStripe] Web Stripe not available, returning stubs');
      return {
        initPaymentSheet: async () => ({ error: { message: 'Stripe not available' } }),
        presentPaymentSheet: async () => ({ error: { code: 'NotAvailable', message: 'Stripe not available' } }),
        isPlatformPaySupported: async () => false,
        confirmPayment: async () => ({ error: { message: 'Stripe not available' } }),
      };
    }
  }
  
  // Use native Stripe implementation
  try {
    const { useStripe: useStripeNative } = require('@stripe/stripe-react-native');
    return useStripeNative();
  } catch (e) {
    console.warn('[useStripe] Native Stripe not available, returning stubs');
    return {
      initPaymentSheet: async () => ({ error: { message: 'Stripe not available' } }),
      presentPaymentSheet: async () => ({ error: { code: 'NotAvailable', message: 'Stripe not available' } }),
      isPlatformPaySupported: async () => false,
      confirmPayment: async () => ({ error: { message: 'Stripe not available' } }),
    };
  }
}

// Also export isPlatformPaySupported for direct use
export async function isPlatformPaySupported(): Promise<boolean> {
  if (Platform.OS === 'web') {
    // On web, check for Apple Pay / Google Pay support
    try {
      const { loadStripe } = require('@stripe/stripe-js');
      const stripePublishableKey = process.env.EXPO_PUBLIC_STRIPE_PUBLISHABLE_KEY;
      if (!stripePublishableKey) return false;
      
      const stripe = await loadStripe(stripePublishableKey);
      if (!stripe) return false;
      
      const paymentRequest = stripe.paymentRequest({
        country: 'US',
        currency: 'usd',
        total: {
          label: 'Test',
          amount: 100,
        },
      });
      
      const result = await paymentRequest.canMakePayment();
      return !!result;
    } catch {
      return false;
    }
  }
  
  // Native implementation
  try {
    const { isPlatformPaySupported: isPlatformPaySupportedNative } = require('@stripe/stripe-react-native');
    return await isPlatformPaySupportedNative();
  } catch {
    return false;
  }
}

// Export confirmPayment for direct use
export async function confirmPayment(clientSecret: string): Promise<{ error?: { message: string } }> {
  if (Platform.OS === 'web') {
    try {
      const { loadStripe } = require('@stripe/stripe-js');
      const stripePublishableKey = process.env.EXPO_PUBLIC_STRIPE_PUBLISHABLE_KEY;
      if (!stripePublishableKey) {
        return { error: { message: 'Stripe not configured' } };
      }
      
      const stripe = await loadStripe(stripePublishableKey);
      if (!stripe) {
        return { error: { message: 'Failed to load Stripe' } };
      }
      
      const { error } = await stripe.confirmPayment({
        clientSecret,
        confirmParams: {
          return_url: window.location.href,
        },
        redirect: 'if_required',
      });
      
      if (error) {
        return { error: { message: error.message || 'Payment confirmation failed' } };
      }
      
      return {};
    } catch (err: any) {
      return { error: { message: err.message || 'Payment confirmation failed' } };
    }
  }
  
  // Native implementation
  try {
    const { confirmPayment: confirmPaymentNative } = require('@stripe/stripe-react-native');
    return await confirmPaymentNative(clientSecret);
  } catch (err: any) {
    return { error: { message: err.message || 'Payment confirmation failed' } };
  }
}

export default useStripe;

